import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Anchor, Button, Spin } from "antd";
import { useEffect, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { PageHeader } from "../../ui";
import { MarkdownView } from "./MarkdownView";
import { useManualGeneration, useManualIndex, useManualOwner } from "./manualHooks";
import type { ManualSection } from "./types";
import "./manual.css";

function tocItems(sections: ManualSection[]) {
  const item = (s: ManualSection) => ({ key: s.id, href: `#${encodeURIComponent(s.anchor)}`, title: s.title });
  return sections.filter((s) => !s.parent).map((s) => ({
    ...item(s), children: sections.filter((c) => c.parent === s.id).map(item),
  }));
}

export function ManualPage() {
  const { module = "" } = useParams();
  const location = useLocation();
  const api = useApi();
  const client = useQueryClient();
  const index = useManualIndex();
  const [iss, sub] = useManualOwner();
  const generation = useManualGeneration();
  const article = useRef<HTMLDivElement>(null);
  const view = index.data?.view;
  const doc = useQuery({
    queryKey: ["manual", iss, sub, view, module, generation],
    queryFn: ({ signal }) => api.manualDoc(module, signal),
    enabled: !!view,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
  useEffect(() => {
    if (doc.data && view && doc.data.view !== view) {
      void client.invalidateQueries({ queryKey: ["manual-index", iss, sub] });
    }
  }, [client, doc.data, iss, sub, view]);

  useEffect(() => {
    if (!doc.data || doc.data.view !== view || !location.hash) return;
    let anchor: string;
    try { anchor = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    const target = article.current?.querySelectorAll<HTMLElement>("[id]");
    const heading = Array.from(target ?? []).find((el) => el.id === anchor);
    if (!heading) return;
    heading.scrollIntoView({ block: "start", behavior: "smooth" });
    heading.classList.add("manual-highlight");
    const timer = window.setTimeout(() => heading.classList.remove("manual-highlight"), 2000);
    return () => {
      window.clearTimeout(timer);
      heading.classList.remove("manual-highlight");
    };
  }, [doc.data, location.hash, view]);

  if (index.isError || doc.isError) {
    const denied = doc.error instanceof ApiError && doc.error.status === 404;
    return <Alert type="error" showIcon message={denied
      ? "このマニュアルを見る権限がありません" : "マニュアルを取得できませんでした"} />;
  }
  if (!doc.data || doc.data.view !== view) return <Spin />;
  return (
    <div className="manual-page">
      <PageHeader title={doc.data.label} secondary={<Link to="/manual"><Button>マニュアル一覧</Button></Link>} />
      <div className="manual-columns">
        <aside aria-label="目次"><Anchor affix={false} items={tocItems(doc.data.sections)} /></aside>
        <div ref={article}><MarkdownView doc={doc.data} /></div>
      </div>
    </div>
  );
}
